import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Grid3x3Icon } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { formatRelativeTime } from "@/shared/lib/utils";
import { matrixItemMatchesSearch, matrixQueries } from "@/domains/matrix";

type MatrixOverviewCardsProps = {
  search: string;
};

export function MatrixOverviewCards({ search }: MatrixOverviewCardsProps) {
  const { data } = useQuery(matrixQueries.list());
  const matrices = (data ?? []).filter((item) =>
    matrixItemMatchesSearch(item, search),
  );

  if (matrices.length === 0) return null;

  return (
    <>
      {matrices.map((matrix) => (
        <Link
          key={matrix.collectionId}
          to={`/matrix/${matrix.collectionId}`}
          className="group"
        >
          <Card className="hover:ring-primary/20 transition-shadow hover:shadow-md hover:ring-1">
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="bg-primary/10 text-primary flex size-6 shrink-0 items-center justify-center rounded-md">
                    <Grid3x3Icon className="size-3.5" aria-hidden />
                  </div>
                  <CardTitle className="min-w-0 truncate text-base">
                    {matrix.name}
                  </CardTitle>
                  <Badge variant="secondary" className="text-xs">
                    Matrix
                  </Badge>
                </div>
                <CardDescription>
                  {matrix.rowCount} {matrix.rowCount === 1 ? "row" : "rows"}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {/* The list carries only the update stamp; "created" would be a lie after a rename. */}
              <div className="text-muted-foreground text-sm">
                Updated {formatRelativeTime(matrix.updatedAt)}
              </div>
            </CardContent>
            <CardFooter className="text-primary text-xs">
              <span className="group-hover:underline">Open matrix -&gt;</span>
            </CardFooter>
          </Card>
        </Link>
      ))}
    </>
  );
}
